import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import i18n from '@/utils/i18n';
import { useGetMerchantOrderSharedLinksForEventTableQuery } from '@/graphql/queries/getMerchantOrderSharedLinksForEventTable.generated';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import OrderStatusBadge from '../EventOrder/components/OrderStatusBadge';
import { Routes } from '@/utils/routes';
import { buildOrderFormUrl } from '@/views/Order/utils/buildOrderFormUrl';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import SvgIcon from '@/components/Icon/SvgIcon';
import {
  EVENT_ORDER_ALLOWED_STATUSES,
  getStatusLabelKeys,
} from '../EventOrder/components/orderStatusConfig';
import {
  MerchantOrderStatusEnum,
  MerchantOrderItemUnit,
  type MerchantOrderSharedLinksFilter,
} from '@/graphql/generatedTypes';
import { Skeleton } from '@/components/ui/skeleton';
import { ContainerCountCell } from '@/components/ContainerCountCell';
import { DataTableFacetedFilter } from '@/views/Order/components/table/TableFacetedFilter';
import { OrderPagination } from '@/views/Order/components/table/pagination';

import type { EventParticipant } from './types';

type EventOrderFormsProps = {
  eventId: string;
  eventParticipants: EventParticipant[];
  merchantOrderFormDeadline?: string | null;
};

export const EventOrderForms: React.FC<EventOrderFormsProps> = ({
  eventId,
  eventParticipants,
  merchantOrderFormDeadline,
}) => {
  // Filter state
  const [selectedEventAccountId, setSelectedEventAccountId] =
    useState<string>('__all__');
  const [selectedStatus, setSelectedStatus] = useState<string>('__all__');
  const [currentPage, setCurrentPage] = useState(1);

  // Build filter object
  const filter = useMemo((): MerchantOrderSharedLinksFilter | undefined => {
    const f: MerchantOrderSharedLinksFilter = {};
    let hasFilter = false;
    if (selectedEventAccountId && selectedEventAccountId !== '__all__') {
      f.event_account_id = selectedEventAccountId;
      hasFilter = true;
    }
    if (selectedStatus && selectedStatus !== '__all__') {
      f.status = selectedStatus as MerchantOrderStatusEnum;
      hasFilter = true;
    }
    return hasFilter ? f : undefined;
  }, [selectedEventAccountId, selectedStatus]);

  const rowsPerPage = 10;

  // Server-side pagination
  const skip = (currentPage - 1) * rowsPerPage;
  const take = rowsPerPage;

  const { data, loading, error } =
    useGetMerchantOrderSharedLinksForEventTableQuery({
      variables: { eventId, filter, skip, take },
    });

  // Event account filter options
  const eventAccountOptions = useMemo(() => {
    return eventParticipants.map((p) => {
      const name = p.name || p.id;
      return { value: p.id, getLabel: () => name };
    });
  }, [eventParticipants]);

  // Status filter options
  const statusOptions = useMemo(() => {
    const labelKeys = getStatusLabelKeys('admin');
    return EVENT_ORDER_ALLOWED_STATUSES.map((status) => {
      const labelKey = labelKeys[status] || 'EventOrder:statusOpen';
      return {
        value: status,
        getLabel: () => i18n.t(labelKey),
      };
    });
  }, []);

  const response = data?.getMerchantOrderSharedLinksForEvent;
  const ordersWithData = response?.eventMerchantorderSharedLinks || [];
  const total = response?.total || 0;
  const containerTypes = useMemo(
    () => data?.event?.allowed_container_types || [],
    [data?.event?.allowed_container_types],
  );

  // Container type packing unit map
  const containerTypeMap = useMemo(() => {
    const map = new Map<number, number>();
    containerTypes.forEach((ct) => {
      map.set(ct.id, ct.packing_unit || 1);
    });
    return map;
  }, [containerTypes]);

  // Calculate container counts
  const calculateContainerCounts = useMemo(() => {
    return (
      items:
        | Array<{
            type_id: number;
            amount_int: number;
            unit: MerchantOrderItemUnit;
          }>
        | null
        | undefined,
    ) => {
      if (!items || items.length === 0) {
        return { amountOfBoxes: 0, amountOfContainers: 0 };
      }

      let amountOfBoxes = 0;
      let amountOfContainers = 0;

      items.forEach((item) => {
        const packingUnit = containerTypeMap.get(item.type_id) || 1;

        if (item.unit === MerchantOrderItemUnit.Boxes) {
          amountOfBoxes += item.amount_int;
          amountOfContainers += item.amount_int * packingUnit;
        } else {
          amountOfContainers += item.amount_int;
          const boxesFromContainers = Math.floor(item.amount_int / packingUnit);
          amountOfBoxes += boxesFromContainers;
        }
      });

      return { amountOfBoxes, amountOfContainers };
    };
  }, [containerTypeMap]);

  const isMerchantOrderFormExpired = merchantOrderFormDeadline
    ? new Date(merchantOrderFormDeadline) < new Date()
    : false;

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  // Handle copying link to clipboard
  const handleCopyLink = async (
    e: React.MouseEvent,
    sharedLinkId: string,
    eventAccountName: string | null,
  ) => {
    e.stopPropagation();

    const fullLink = buildOrderFormUrl(sharedLinkId);

    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success(i18n.t('Common:linkCopied'), {
        description: eventAccountName
          ? i18n.t('Order:toastFormLinkCopied', { name: eventAccountName })
          : i18n.t('Order:toastFormLinkCopiedGeneric'),
      });
    } catch (err: any) {
      toast.error(i18n.t('Common:copyLinkError'), {
        description: err.message || i18n.t('Common:unknownError'),
      });
    }
  };

  // Reset page on filter change
  const handleEventAccountChange = (value: string) => {
    setSelectedEventAccountId(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  return (
    <div className="tw:space-y-4">
      <h2 className="tw:text-2xl tw:font-semibold">
        {i18n.t('Events:orderFormsTitle')}
      </h2>

      {/* Filters */}
      <div className="tw:flex tw:gap-2 tw:items-center">
        <DataTableFacetedFilter
          title={i18n.t('Event:eventAccount')}
          options={eventAccountOptions}
          value={selectedEventAccountId}
          onValueChange={handleEventAccountChange}
        />
        <DataTableFacetedFilter
          title={i18n.t('Events:orderFormsOrderStatus')}
          options={statusOptions}
          value={selectedStatus}
          onValueChange={handleStatusChange}
        />
      </div>

      {error ? (
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <p className="tw:text-red-500">
            {i18n.t('Common:error')}: {error.message}
          </p>
        </div>
      ) : ordersWithData.length === 0 && currentPage === 1 && !loading ? (
        <div className="tw:flex tw:justify-center tw:items-center tw:py-12">
          <p className="tw:text-gray-500">{i18n.t('Events:orderFormsEmpty')}</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n.t('Events:orderFormsOrderId')}</TableHead>
                <TableHead>{i18n.t('Event:eventAccount')}</TableHead>
                <TableHead>{i18n.t('Events:orderFormsOrderStatus')}</TableHead>
                <TableHead>{i18n.t('Events:orderFormsSalesOrder')}</TableHead>
                <TableHead>
                  {i18n.t('Events:orderFormsContainerCount')}
                </TableHead>
                <TableHead>{i18n.t('Events:orderFormsItemTypes')}</TableHead>
                <TableHead className="tw:text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-6 tw:w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="tw:h-4 tw:w-24" />
                      </TableCell>
                      <TableCell className="tw:text-right">
                        <Skeleton className="tw:h-8 tw:w-32 tw:ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                : ordersWithData.map((link) => {
                    const merchantOrder = link.merchant_order;
                    const deliveryNoteId = merchantOrder?.delivery_note_id;
                    const eventAccountName = link.event_account?.name || null;
                    const items = merchantOrder?.latest_revision?.containers;
                    const { amountOfBoxes, amountOfContainers } =
                      calculateContainerCounts(items);
                    const itemTypeLines =
                      merchantOrder?.latest_revision?.items ?? [];

                    return (
                      <TableRow key={link.id}>
                        <TableCell>
                          {deliveryNoteId && link.merchant_order?.id ? (
                            <Link
                              to={Routes.eventOrderDetail(
                                eventId,
                                link.merchant_order.id,
                              )}
                              className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              #{deliveryNoteId}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {link.event_account ? (
                            <Link
                              to={Routes.eventAccountDetail(
                                link.event_account.id,
                              )}
                              className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              {eventAccountName || '-'}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {merchantOrder ? (
                            <OrderStatusBadge
                              status={merchantOrder.status}
                              expired={isMerchantOrderFormExpired}
                              variant="admin"
                            />
                          ) : (
                            <OrderStatusBadge
                              status={MerchantOrderStatusEnum.OnHold}
                              expired={isMerchantOrderFormExpired}
                              variant="admin"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {link.sales_order_url ? (
                            <a
                              href={link.sales_order_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                            >
                              {i18n.t('Order:viewInZoho')}
                              <FaExternalLinkAlt className="tw:text-xs" />
                            </a>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <ContainerCountCell
                            amountOfBoxes={amountOfBoxes}
                            amountOfContainers={amountOfContainers}
                          />
                        </TableCell>
                        <TableCell>
                          {itemTypeLines.length === 0 ? (
                            <span className="tw:text-muted-foreground">-</span>
                          ) : (
                            <ul className="tw:space-y-0.5">
                              {itemTypeLines.map((line) => (
                                <li
                                  key={`${line.item_type_id}-${line.item_type?.id ?? ''}`}
                                  className="tw:text-sm tw:whitespace-nowrap"
                                >
                                  <span className="tw:font-medium">
                                    {line.amount_int}
                                  </span>
                                  {' × '}
                                  {line.item_type?.name ??
                                    `#${line.item_type_id}`}
                                </li>
                              ))}
                            </ul>
                          )}
                        </TableCell>
                        <TableCell className="tw:text-right tw:min-w-[220px]">
                          <div className="tw:flex tw:justify-end tw:min-h-[36px] tw:items-center">
                            <Button
                              variant="outline"
                              onClick={(e) =>
                                handleCopyLink(e, link.id, eventAccountName)
                              }
                              disabled={loading}
                            >
                              <SvgIcon name="copy-content" />
                              {i18n.t('Event:copyOrderFormLink')}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>

          <OrderPagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        </>
      )}
    </div>
  );
};
